<template>
  <div class="filter-wrapper">
    <div class="search-wrapper">
      <div class="filter-input-area">
        <span
          v-if="isLiveMode"
          class="filter-live-badge"
          :aria-label="t('filter.liveBadgeAriaLabel')"
          >{{ t('filter.liveBadge') }}</span
        >
        <rbz-form-input
          name="filterInput"
          class="filter-input"
          :class="{ 'filter-input--live': isLiveMode }"
          :icon="query ? 'IconClose' : ''"
          icon-location="right"
          :icon-clickable="true"
          v-model:inputValue="query"
          @blur="onFilterInputFieldBlur()"
          @keydown.enter="
            syncTime('input');
            search();
          "
          @icon-clicked="onFilterInputIconClick()"
          :ignore-update-in-process="true"
          :placeholder="t('filter.search')"
          data-qa="filter-input"
        />
      </div>
      <div class="date-picker-wrapper">
        <div v-if="isLiveMode" ref="liveIndicatorWrapperRef" class="live-indicator-wrapper">
          <live-time-indicator
            :label="liveLabel"
            :timezone="timezoneOffset"
            :countdown="liveCountdown"
            :total-interval="liveRefreshSeconds"
            @open="openPickerFromLiveIndicator"
            @close="stopLiveMode"
          />
        </div>
        <!-- v-show keeps the picker mounted so its internal state survives live↔stopped transitions -->
        <rbz-time-picker
          v-show="!isLiveMode"
          @update:date="
            date = $event;
            syncTime('datepicker');
          "
          @update:live-mode="handleLiveModeChange"
          :default-time="defaultTime"
          ref="rbzTimePicker"
        />
      </div>
    </div>
    <div class="buttons-wrapper">
      <rbz-form-button
        variant="tertiary"
        icon="IconSearch"
        data-qa="search-button"
        class="search-button"
        @button-click="search()"
        :disabled="searchDisabled"
        v-b-tooltip="t('filter.search')"
        :ignore-update-in-process="true"
      />
      <rbz-form-button
        variant="tertiary"
        icon="IconDuplicate"
        data-qa="copy-query-button"
        class="copy-query-button"
        @button-click="copyQuery()"
        v-b-tooltip="t('filter.copyQuery')"
        :ignore-update-in-process="true"
      />
      <slot name="additionalButtons" />
      <rbz-dropdown-menu trigger-button-icon="IconMoreVertical" ref="dropdownMenu">
        <template #dropdownMenuContent>
          <a target="_blank" tabindex="-1" :href="filterInformationPath">
            <menu-list-item
              :title="t('filter.filterInformation')"
              classes="ignore-fix-focus"
              data-qa="filter-information-button"
              class="filter-information-button"
              @item-click="openFilterInformation"
            />
          </a>
          <menu-list-item
            v-if="showQueryHistory"
            :title="t('filter.queryHistory')"
            :is-loading="isQueryHistoryLoading"
            :disabled="isQueryHistoryLoading"
            classes="ignore-fix-focus"
            data-qa="history-button"
            class="history-button"
            @item-click="
              openHistoryModal();
              dropdownMenu.closeMenu();
            "
          />
          <menu-list-item
            v-if="showQueryHistory"
            :title="t('filter.applyPreviousQuery')"
            :is-loading="isQueryHistoryLoading"
            :disabled="isQueryHistoryLoading"
            classes="ignore-fix-focus"
            data-qa="apply-previous-query-button"
            class="apply-previous-query-button"
            @item-click="
              revertQuery();
              dropdownMenu.closeMenu();
            "
          />
        </template>
      </rbz-dropdown-menu>
    </div>
    <rbz-modal-dialog
      :show="showHistoryModal"
      :title="t('filter.queryHistory')"
      :hide-footer="true"
      @closeModal="showHistoryModal = false"
      size="large"
    >
      <template #content>
        <div class="modal-body-content-wrapper">
          <rbz-loader :loading="!!isQueryHistoryLoading" :data-exists="true" height="200px" />
          <rbz-table
            v-if="!isQueryHistoryLoading"
            :columns="historyModalColumns"
            :table-data="queryHistoryData"
            :show-top-navbar="false"
            :row-buttons="historyModalRowButtons"
            data-qa="filter-history-table"
            @restore-row-button-clicked="restoreQuery($event as QueryHistory)"
          />
        </div>
      </template>
    </rbz-modal-dialog>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import RbzFormButton from './RbzFormButton.vue';
import RbzFormInput from '@/components/RbzFormInput.vue';
import RbzTimePicker from 'ui-component-library/rbz/components/RbzTimePicker.vue';
import LiveTimeIndicator from '@/components/LiveTimeIndicator.vue';
import { useRoute, useRouter } from 'vue-router';
import _ from 'lodash';
import RbzModalDialog from '@/components/RbzModalDialog.vue';
import RbzTable from 'ui-component-library/rbz/components/RbzTable.vue';
import DatasetsUtils from '@/helpers/DatasetsUtils';
import { QUERY_FILTER_DOCS_URL } from '@/types/externalUrls';
import DateTimeUtils from 'ui-component-library/helpers/DateTimeUtils';
import RbzDropdownMenu from '@/components/RbzDropdownMenu.vue';
import { useI18n } from 'vue-i18n';
import { RbzTableColumnOptions, RbzTableRowButton } from 'ui-component-library/rbz/types';
import RbzLoader from '@/components/RbzLoader.vue';
import MenuListItem from 'ui-component-library/rbz/components/MenuListItem.vue';
import type { LiveModePayload } from 'ui-component-library/rbz/composables/useTimeRangePicker';
import { useLiveModeStore } from '@/store/LiveModeStore';

const { t } = useI18n();

interface RbzFilterProps {
  searchDisabled?: boolean;
  defaultTime?: number;
  queryHistory: string[];
  isQueryHistoryLoading: boolean;
  showQueryHistory?: boolean;
}

const props = withDefaults(defineProps<RbzFilterProps>(), {
  searchDisabled: false,
  showQueryHistory: true,
});

type QueryHistory = {
  id: string;
  query: string;
};

const liveModeStore = useLiveModeStore();
const { isLiveMode, liveLabel, liveRefreshSeconds, liveCountdown } = storeToRefs(liveModeStore);

const route = useRoute();
const router = useRouter();
const query = ref<string>('');
const date = ref<[string, string]>([null, null]);
const rbzTimePicker = ref<typeof RbzTimePicker>(null);
const liveIndicatorWrapperRef = ref<HTMLElement | null>(null);

const timezoneOffset = computed(() => {
  const offset = -new Date().getTimezoneOffset();
  const absOffset = Math.abs(offset);
  const hours = Math.floor(absOffset / 60);
  const minutes = absOffset % 60;
  const sign = offset >= 0 ? '+' : '-';
  return `GMT${sign}${hours}${minutes ? ':' + String(minutes).padStart(2, '0') : ''}`;
});

watch(
  () => liveModeStore.refreshTrigger,
  () => {
    if (liveModeStore.isLiveMode) {
      const now = new Date();
      const start = new Date(now.getTime() - liveModeStore.liveMinutes * 60_000);
      date.value = [start.toISOString(), now.toISOString()];
      search(true, false);
    }
  }
);

const openPickerFromLiveIndicator = () => {
  const el = liveIndicatorWrapperRef.value;
  if (!el) return;
  const rect = el.getBoundingClientRect();
  rbzTimePicker.value?.openPanelWithState(
    true,
    liveModeStore.liveMinutes,
    liveModeStore.liveRefreshSeconds,
    rect,
    el
  );
};

/** Replace any existing timestamp filter in the display query with a "last N minutes" label. */
const setLiveTimestampLabel = (minutes: number) => {
  let filters = query.value ? query.value.split(/, /) : [];
  filters = filters.filter((f) => !f.trim().startsWith('timestamp'));
  filters.push(`timestamp last ${minutes} minutes`);
  query.value = filters.join(', ');
};

const handleLiveModeChange = (payload: LiveModePayload) => {
  if (payload.enabled && payload.minutes != null && payload.minutes > 0) {
    setLiveTimestampLabel(payload.minutes);

    liveModeStore.startLiveMode({
      minutes: payload.minutes,
      label: payload.label,
      refreshIntervalSeconds: payload.refreshIntervalSeconds,
    });

    const now = new Date();
    const start = new Date(now.getTime() - payload.minutes * 60_000);
    date.value = [start.toISOString(), now.toISOString()];
    search(true, false);
  } else if (!payload.enabled && liveModeStore.isLiveMode) {
    stopLiveMode();
  }
};

const stopLiveMode = () => {
  liveModeStore.stopLiveMode();

  // date.value was already set to the user's chosen absolute range via @update:date
  // before this function is called — do not override it with the old live window.
  nextTick(() => {
    rbzTimePicker.value?.changeTime(date.value);
    rbzTimePicker.value?.disableLive();
    syncTime('datepicker');
    search(true, false);
  });
};

let queryHistoryData = ref<QueryHistory[]>([]);
watch(
  () => props.queryHistory,
  (value) => {
    queryHistoryData.value = _.map(value, (item) => {
      return {
        id: DatasetsUtils.generateUUID2(),
        query: item,
      };
    });
  },
  {
    deep: true,
    immediate: true,
  }
);
const showHistoryModal = ref<boolean>(false);
const historyModalColumns = ref<RbzTableColumnOptions[]>([
  {
    id: 'query',
    title: t('filter.query'),
    fieldNames: ['query'],
    cellContentClasses: 'vertical-scroll multi-line white-space-pre-line',
  },
]);
const historyModalRowButtons = ref<RbzTableRowButton[]>([
  {
    id: 'restore',
    tooltip: t('filter.restoreTableTooltip'),
    icon: 'IconRestore',
    ignoreUpdateInProcess: true,
  },
]);

interface RbzFilterEmits {
  (e: 'search', value: string, saveHistory: boolean): void;
  (e: 'clear-filter'): void;
  (e: 'blur-filter', inputValue: string): void;
}

const emit = defineEmits<RbzFilterEmits>();

const search = (force: boolean = false, saveHistory: boolean = true) => {
  if (!force && props.searchDisabled) {
    return;
  }
  const constructedQuery = constructQuery();
  router.replace({ query: { filters: constructedQuery } });
  emit('search', constructedQuery, saveHistory);
};

const constructTimeQuery = () => {
  const startDate = date.value[0];
  const startDateISO = startDate?.split('.')?.[0]?.replace('T', ' ') || '';
  const endDate = date.value[1];
  const endDateISO = endDate?.split('.')?.[0]?.replace('T', ' ') || '';
  return `timestamp between ${startDateISO} and ${endDateISO}`;
};

const constructQuery = () => {
  let filters = query.value ? query.value.split(/, /) : [];
  const timestampFilterExists = filters.some((filter) => {
    return filter.trim().startsWith('timestamp');
  });
  if (timestampFilterExists) {
    filters = _.filter(filters, (filter) => {
      return !filter.trim().startsWith('timestamp');
    });
  }
  filters.push(constructTimeQuery());
  return filters.join(', ');
};
const onFilterInputFieldBlur = () => {
  syncTime('input');
  emit('blur-filter', query.value);
};
const onFilterInputIconClick = () => {
  clearQuery();
  emit('clear-filter');
};
const clearQuery = () => {
  query.value = '';
};

const copyQuery = () => {
  navigator.clipboard.writeText(constructQuery());
};

const openHistoryModal = () => {
  showHistoryModal.value = true;
};

const revertQuery = () => {
  const newQuery = queryHistoryData.value[0]?.query;
  if (newQuery) {
    query.value = newQuery;
    syncTime('input');
  }
};

const restoreQuery = (queryHistoryEntry: QueryHistory) => {
  query.value = queryHistoryEntry.query;
  syncTime('input');
  showHistoryModal.value = false;
};

const filterInformationPath = computed(() => {
  return QUERY_FILTER_DOCS_URL;
});
const openFilterInformation = () => {
  window.open(filterInformationPath.value, '_blank');
  dropdownMenu.value.closeMenu();
};

const dropdownMenu = ref(null);

const addFilter = (filterToAdd: string) => {
  const splitFilter = filterToAdd.split(/=/);
  const operator = splitFilter.shift();
  const operand = splitFilter.join('=');
  const filters: string[] = query.value ? query.value.split(/, /) : [];
  const filterExists = filters.some((filter) => {
    const currentSplitFilter = filter.split(/=/);
    const currentOperator = currentSplitFilter.shift();
    const currentOperand = currentSplitFilter.join('=');
    return operator === currentOperator && operand === currentOperand;
  });
  if (filterExists) {
    return;
  }
  filters.push(filterToAdd);
  query.value = filters.join(', ');
};

const removeFilter = (filterToRemove: string) => {
  const splitFilter = filterToRemove.split(/=/);
  const operator = splitFilter.shift();
  const operand = splitFilter.join('=');
  const filters: string[] = query.value ? query.value.split(/, /) : [];
  const updatedFilters = filters.filter((filter) => {
    const currentSplitFilter = filter.split(/=/);
    const currentOperator = currentSplitFilter.shift();
    const currentOperand = currentSplitFilter.join('=');
    return !(operator === currentOperator && operand === currentOperand);
  });
  query.value = updatedFilters.join(', ');
};

const addServerGroupFilter = (filterToAdd: string) => {
  const filters: string[] = query.value ? query.value.split(/, /) : [];

  const filtersWithOutServerGroup = filters.filter((filter) => {
    const currentSplitFilter = filter.split(/=/);
    const currentOperator = currentSplitFilter[0].trim();
    return currentOperator !== 'server_group';
  });
  if (filterToAdd === 'server_group=""') {
    query.value = filtersWithOutServerGroup.join(', ');
  } else {
    filtersWithOutServerGroup.push(filterToAdd);
    filters.push(filterToAdd);
    query.value = filtersWithOutServerGroup.join(', ');
  }
};

const syncTime = (source: 'input' | 'datepicker') => {
  let filters = query.value ? query.value.split(/, /) : [];
  const timestampFilterExists = filters.some((filter) => {
    return filter.trim().startsWith('timestamp');
  });
  if (!timestampFilterExists) {
    return;
  }
  if (source === 'input') {
    // In live mode the timestamp is owned by the live timer — reset any manual edits
    if (isLiveMode.value) {
      setLiveTimestampLabel(liveModeStore.liveMinutes);
      return;
    }

    let startDate;
    let endDate;
    const lastIndexBetween = filters.findLastIndex((filter) => {
      return filter.trim().startsWith('timestamp between');
    });
    const lastIndexGreaterThan = filters.findLastIndex((filter) => {
      return filter.trim().startsWith('timestamp>');
    });
    const lastIndexLowerThan = filters.findLastIndex((filter) => {
      return filter.trim().startsWith('timestamp<');
    });
    if (lastIndexBetween > -1) {
      const splitDates = filters[lastIndexBetween].split('between')?.[1]?.split('and');
      startDate = splitDates?.[0];
      endDate = splitDates?.[1];
    }
    if (lastIndexGreaterThan > -1 && lastIndexGreaterThan > lastIndexBetween) {
      startDate = filters[lastIndexGreaterThan].split('>')?.[1] || startDate;
    }
    if (lastIndexLowerThan > -1 && lastIndexLowerThan > lastIndexBetween) {
      endDate = filters[lastIndexLowerThan].split('<')?.[1] || endDate;
    }
    let newStartDate;
    try {
      newStartDate = DateTimeUtils.adjustDateToTimezone(new Date(startDate))?.toISOString();
    } catch {
      newStartDate = date.value[0];
    }
    let newEndDate;
    try {
      newEndDate = DateTimeUtils.adjustDateToTimezone(new Date(endDate))?.toISOString();
    } catch {
      newEndDate = date.value[1];
    }
    const newDate = [newStartDate, newEndDate];
    date.value = newDate as [string, string];
    rbzTimePicker.value?.changeTime(newDate);
  }
  if (source === 'datepicker') {
    if (isLiveMode.value) {
      return;
    }
    filters = _.filter(filters, (filter) => {
      return !filter.trim().startsWith('timestamp');
    });
    filters.push(constructTimeQuery());
    query.value = filters.join(', ');
  }
};

const changeTime = (newDate: [string, string]) => {
  if (!_.isEqual(date.value, newDate)) {
    date.value = newDate;
    rbzTimePicker.value?.changeTime(newDate);
    search(true);
  }
};

const setDataFromRoute = () => {
  if (liveModeStore.isLiveMode) return;

  if (route.query?.filters) {
    query.value = route.query.filters.toString();
    syncTime('input');
  } else {
    rbzTimePicker.value?.resetTimeToDefault();
    query.value = '';
  }
};

defineExpose({
  addServerGroupFilter,
  addFilter,
  removeFilter,
  changeTime,
  search,
  setDataFromRoute,
});

onMounted(() => {
  liveModeStore.registerFilter();

  if (liveModeStore.isLiveMode) {
    // Restore non-timestamp filters from the route when navigating between analytics pages in live mode
    if (route.query?.filters) {
      const routeFilters = route.query.filters.toString().split(/, /);
      const nonTimestampFilters = routeFilters.filter((f) => !f.trim().startsWith('timestamp'));
      query.value = nonTimestampFilters.join(', ');
    }

    setLiveTimestampLabel(liveModeStore.liveMinutes);

    const now = new Date();
    const start = new Date(now.getTime() - liveModeStore.liveMinutes * 60_000);
    date.value = [start.toISOString(), now.toISOString()];
    search(true, false);
  } else {
    setDataFromRoute();
  }
});

onUnmounted(() => {
  liveModeStore.unregisterFilter();
});
</script>

<style lang="scss" scoped>
@use 'ui-component-library/assets/styles/colors' as colors;
@use 'sass:map';

.filter-wrapper {
  display: flex;
  gap: 8px;
  margin-top: 8px;
}

.search-wrapper {
  align-items: center;
  display: flex;
  flex-grow: 1;
  gap: 8px;
}

.filter-input-area {
  flex-grow: 1;
  position: relative;
  width: auto;
}

.filter-live-badge {
  background: map.get(colors.$colors, 'hint-of-green');
  border-radius: 4px;
  color: map.get(colors.$colors, 'shamrock');
  font-size: 10px;
  font-weight: 400;
  left: 13px;
  line-height: 15px;
  padding: 2px 6px;
  pointer-events: none;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;
}

.filter-input {
  width: 100%;
}

.filter-input--live {
  :deep(.form-input) {
    border-color: map.get(colors.$colors, 'feijoa');
    padding-left: 56px;
  }
}

.date-picker-wrapper {
  align-items: center;
  display: flex;
}

:deep(.buttons-wrapper) {
  align-items: center;
  display: flex;
  gap: 2px;

  :deep(.rbz-button.tertiary) {
    height: 40px;
    width: 40px;
  }
}

.modal-wrapper {
  .modal-body-content-wrapper {
    min-height: 200px;
    position: relative;
  }
}

:deep(.rbz-table) {
  .multi-line.vertical-scroll {
    max-height: 4.5rem;
  }
}
</style>
